import { onMounted, onBeforeUnmount } from 'vue'

interface RevealOptions {
  rootMargin?: string
  threshold?: number | number[]
  once?: boolean
  staggerInterval?: number
}

/*
  Single shared observer used across the app. Per-element options (staggerInterval, once)
  are stored in WeakMaps so the observer callback can respect per-element behavior while
  avoiding multiple observer instances.
*/
let sharedObserver: IntersectionObserver | null = null
const elementOptions = new WeakMap<Element, Required<Pick<RevealOptions, 'once' | 'staggerInterval'>>>()
const revealQueue: Element[] = []

function ensureObserver() {
  if (sharedObserver) return sharedObserver
  sharedObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = entry.target as HTMLElement
        if (!revealQueue.includes(el)) revealQueue.push(el)
      }
    })

    if (revealQueue.length) {
      // Sort by dataset reveal index for deterministic stagger ordering
      revealQueue.sort((a, b) => ((a as HTMLElement).dataset.revealIndex || '0').localeCompare((b as HTMLElement).dataset.revealIndex || '0'))
      // Process queue with per-element stagger
      let accumulatedDelay = 0
      while (revealQueue.length) {
        const el = revealQueue.shift() as HTMLElement
        const opts = elementOptions.get(el) || { once: true, staggerInterval: 80 }
        const delay = accumulatedDelay
        setTimeout(() => {
          el.classList.add('is-revealed')
          if (opts.once) sharedObserver?.unobserve(el)
        }, delay)
        accumulatedDelay += opts.staggerInterval
      }
    }
  }, { rootMargin: '0px', threshold: 0.1 })
  return sharedObserver
}

export function useReveal(el: () => HTMLElement | undefined, options: RevealOptions = {}) {
  const targetGetter = el

  onMounted(() => {
    const target = targetGetter()
    if (!target) return
    target.classList.add('reveal-base')
    if (!target.dataset.revealIndex) target.dataset.revealIndex = String(performance.now())
    elementOptions.set(target, { once: options.once ?? true, staggerInterval: options.staggerInterval ?? 80 })
    ensureObserver().observe(target)
  })

  onBeforeUnmount(() => {
    const target = targetGetter()
    if (target && sharedObserver) sharedObserver.unobserve(target)
  })
}

// Vue directive for convenience
export const revealDirective = {
  mounted(el: HTMLElement, binding: any) {
    const opts: RevealOptions = binding.value || {}
    el.classList.add('reveal-base')
    if (!el.dataset.revealIndex) el.dataset.revealIndex = String(performance.now())
    elementOptions.set(el, { once: opts.once ?? true, staggerInterval: opts.staggerInterval ?? 80 })
    ensureObserver().observe(el)
  },
  unmounted(el: HTMLElement) {
    sharedObserver?.unobserve(el)
    elementOptions.delete(el)
  }
}

export default useReveal
